import type {
  BookingMode,
  Interest,
  PlanInput,
  PlanResult,
  Reservation,
  SessionTokens,
  StageStatus,
  VenueDetails,
  VenueReservationInput,
  VenueSummary,
} from "../domain/models";
import { details, fixturePlans, reservation, venues } from "../fixtures/data";
import { ApiError, request, requestAcceptedId, setSession } from "./http";
import { planResultSchema, tokensSchema } from "./schemas";

const fixtures = import.meta.env.VITE_USE_FIXTURES !== "false";
const delay = (ms = 250) => new Promise((resolve) => setTimeout(resolve, ms));
export interface VenueFilters {
  cityId?: string | undefined;
  category?: string | undefined;
  interest?: Interest | undefined;
  amenity?: string | undefined;
  priceBand?: number | undefined;
  openNow?: boolean | undefined;
  query?: string | undefined;
}
const statusMap: Record<string, StageStatus> = {
  pending: "request_pending",
  accepted_payment_pending: "accepted_deposit_required",
  confirmed: "confirmed",
  rejected: "rejected",
  expired: "expired",
  cancelled: "cancelled",
  replacement_required: "replacement_required",
};
const fixtureReservations: Reservation[] = [reservation];

export const api = {
  async searchVenues(filters: VenueFilters): Promise<VenueSummary[]> {
    if (fixtures) {
      await delay();
      return venues.filter(
        (item) =>
          (!filters.category ||
            item.activityCategories.includes(filters.category)) &&
          (!filters.interest || item.interests.includes(filters.interest)) &&
          (!filters.priceBand || item.priceBand === filters.priceBand) &&
          (!filters.openNow || item.isOpen) &&
          (!filters.query ||
            `${item.venueName} ${item.area}`
              .toLowerCase()
              .includes(filters.query.toLowerCase())),
      );
    }
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && typeof value !== "boolean")
        query.set(key, String(value));
      if (typeof value === "boolean") query.set(key, String(value));
    });
    const page = await request<{
      data: Array<
        Omit<VenueSummary, "area" | "interests" | "rating" | "image" | "isOpen">
      >;
    }>(`/api/v1/venues?${query}`);
    return page.data.map((item) => ({
      ...item,
      area: item.branchName,
      interests: [],
      rating: 0,
      image: "",
      isOpen: false,
      bookingModes: item.bookingModes as BookingMode[],
    }));
  },
  async venue(id: string): Promise<VenueDetails> {
    if (fixtures) {
      await delay();
      const value = details.find((item) => item.id === id);
      if (!value) throw new Error("Place not found");
      return value;
    }
    return request(`/api/v1/venues/${id}`);
  },
  async generatePlan(
    input: PlanInput,
    anonymousSessionId: string,
    locked: boolean,
  ): Promise<PlanResult> {
    if (fixtures) {
      await delay(700);
      return fixturePlans(locked, input);
    }
    const result = await request<unknown>("/api/v1/plans/generate", {
      method: "POST",
      body: JSON.stringify({ ...input, anonymousSessionId }),
    });
    return planResultSchema.parse(result) as PlanResult;
  },
  async getPlans(
    requestId: string,
    anonymousSessionId?: string,
  ): Promise<PlanResult> {
    if (fixtures) {
      await delay();
      return fixturePlans(!!anonymousSessionId);
    }
    const suffix = anonymousSessionId
      ? `?anonymousSessionId=${anonymousSessionId}`
      : "";
    return planResultSchema.parse(
      await request(`/api/v1/plans/${requestId}${suffix}`),
    ) as PlanResult;
  },
  async claimPlans(requestId: string, anonymousSessionId: string) {
    if (fixtures) {
      await delay();
      return;
    }
    await request(`/api/v1/plans/${requestId}/claim`, {
      method: "POST",
      body: JSON.stringify(anonymousSessionId),
    });
  },
  async replacements(stageId: string): Promise<VenueSummary[]> {
    if (fixtures) {
      await delay();
      return venues.slice(1, 5);
    }
    return request(`/api/v1/plan-stages/${stageId}/replacements`);
  },
  async updateStage(
    stageId: string,
    body: { startsAt?: string; removed?: boolean; rowVersion: number },
  ) {
    if (fixtures) {
      await delay();
      return;
    }
    await request(`/api/v1/plan-stages/${stageId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  async reservePlan(planId: string): Promise<string> {
    if (fixtures) {
      await delay();
      return reservation.id;
    }
    return requestAcceptedId(`/api/v1/plans/${planId}/reservations`, {
      method: "POST",
      body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
    });
  },
  async createVenueReservation(
    input: VenueReservationInput,
  ): Promise<Reservation> {
    if (fixtures) {
      await delay(500);
      const venue = details.find((item) => item.id === input.venueId);
      if (!venue) throw new Error("Place not found");
      const created: Reservation = {
        id: `direct-${crypto.randomUUID()}`,
        status: "in_progress",
        requestedAt: new Date().toISOString(),
        walkInStages: [],
        stages: [
          {
            venueRequestId: `vr-${crypto.randomUUID()}`,
            venueName: venue.venueName,
            branchName: venue.branchName,
            startsAt: new Date(
              `${input.requestedLocalDate}T${input.requestedLocalTime}:00+03:00`,
            ).toISOString(),
            bookingMode: "request_to_book",
            status: "request_pending",
            paymentStatus: "not_required",
            depositMinor: venue.depositMinor,
            currency: "USD",
          },
        ],
      };
      fixtureReservations.unshift(created);
      return created;
    }
    throw new ApiError({
      status: 501,
      title: "Direct venue booking is not available",
      detail:
        "The current customer API accepts reservations only from generated plan stages. Add a direct venue reservation endpoint to the backend OpenAPI contract before enabling this operation.",
    });
  },
  async startOtp(
    mobile: string,
    dateOfBirth: string,
    confirmed18: boolean,
  ): Promise<string> {
    if (fixtures) {
      await delay();
      return "otp-demo-1";
    }
    const data = await request<{ challengeId: string }>(
      "/api/v1/auth/customer/otp/start",
      {
        method: "POST",
        body: JSON.stringify({ mobile, dateOfBirth, confirmed18 }),
      },
    );
    return data.challengeId;
  },
  async completeOtp(
    challengeId: string,
    code: string,
    anonymousSessionId: string,
  ): Promise<SessionTokens> {
    const tokens = fixtures
      ? {
          accessToken: "fixture-access",
          refreshToken: "fixture-refresh",
          accessExpiresAt: new Date(Date.now() + 900000).toISOString(),
        }
      : tokensSchema.parse(
          await request("/api/v1/auth/customer/otp/complete", {
            method: "POST",
            body: JSON.stringify({ challengeId, code, anonymousSessionId }),
          }),
        );
    await setSession(tokens);
    return tokens;
  },
  async reservations(): Promise<Reservation[]> {
    if (fixtures) {
      await delay();
      return fixtureReservations;
    }
    const rows = await request<{
      data: Array<{
        reservationId: string;
        reservationStatus: string;
        requestedAt: string;
        venueRequestId: string;
        stageStatus: string;
        venueName: string;
        branchName: string;
        startsAt: string;
        depositMinor: number;
        currency: "USD";
      }>;
    }>("/api/v1/me/reservations");
    const grouped = new Map<string, Reservation>();
    rows.data.forEach((item) => {
      const value = grouped.get(item.reservationId) ?? {
        id: item.reservationId,
        status: item.reservationStatus,
        requestedAt: item.requestedAt,
        walkInStages: [],
        stages: [],
      };
      value.stages.push({
        venueRequestId: item.venueRequestId,
        venueName: item.venueName,
        branchName: item.branchName,
        startsAt: item.startsAt,
        bookingMode: "request_to_book",
        status: statusMap[item.stageStatus] ?? "request_pending",
        paymentStatus:
          item.stageStatus === "confirmed"
            ? "paid"
            : item.stageStatus === "accepted_payment_pending"
              ? "required"
              : "not_required",
        depositMinor: item.depositMinor,
        currency: item.currency,
      });
      grouped.set(item.reservationId, value);
    });
    return [...grouped.values()];
  },
  async createPayment(venueRequestId: string, returnUrl: string) {
    if (fixtures) {
      await delay();
      return {
        kind: "redirect",
        redirectUrl: `${location.origin}/payment-return?status=pending&request=${venueRequestId}`,
        providerReference: "mock-whish-001",
      };
    }
    return request<{
      kind: string;
      redirectUrl?: string;
      providerReference: string;
    }>(`/api/v1/reservation-requests/${venueRequestId}/payment`, {
      method: "POST",
      body: JSON.stringify({ idempotencyKey: crypto.randomUUID(), returnUrl }),
    });
  },
  async deleteAccount() {
    if (fixtures) {
      await delay();
      await setSession(null);
      return;
    }
    await request("/api/v1/me", { method: "DELETE" });
    await setSession(null);
  },
};
